import { defaultSerializeQueryArgs, skipToken } from '@reduxjs/toolkit/query';
import { StudyListTag } from 'components/Flux/shared/useStudyActions';
import getProductKey from 'helper/requests/getProductKey';
import { sendInterfaceErrorToSentry } from 'helper/sendErrorToSentry';
import {
  MyStudiesListResult,
  MyStudiesRejectedRow,
  MyStudyCreatorsQuery,
  normalizeCreatorsQuery,
  normalizeMyStudiesQuery,
  normalizeStudyIds,
  toCreatorsWireParams,
  toMyStudiesListResult,
  toMyStudyStatusSnapshotsResult,
  toStatusWireParams,
  toStudyCompaniesSummaryResult,
  toStudyCreatorOptionsResult,
  toWireParams,
} from 'helper/flux/myStudiesAdapter';
import {
  MyStudiesQuery,
  MyStudyStatusSnapshot,
  RawMyStudiesPage,
  RawMyStudyStatus,
  RawStudyCompanies,
  RawStudyCreator,
  StudyCompaniesSummary,
  StudyCreatorOption,
} from 'types/Flux/Personalized/myStudies';
import baseApi, { GenericResponse } from './base';

/** Builder de endpoints do `baseApi`, o mesmo tipo que `injectEndpoints` passa ao callback. */
export type ApiEndpointBuilder = Parameters<Parameters<typeof baseApi.injectEndpoints>[0]['endpoints']>[0];

export interface MyStudiesEndpointsConfig {
  /** Prefixo das rotas, sem barra final (`produtos/previvaz-<produto>/estudos`). */
  root: string;
  /** Tag da lista do produto: as mutations de excluir/cancelar/reenviar a invalidam. */
  listTag: StudyListTag;
}

/**
 * A `product_key` vai dentro da string da URL (como nos demais endpoints do produto): o
 * `baseQuery` a recupera e reescreve por regex em `url` na recuperação silenciosa do 403.
 * Os outros parâmetros seguem em `params` (o `fetchBaseQuery` os anexa com `&`).
 * Lida a cada requisição, nunca no escopo do módulo. A mesma chave serve aos dois produtos.
 */
export const studiesUrl = (root: string, path = '') => (
  `${root}/${path}?product_key=${getProductKey('prevs-personalizado')}`
);

/**
 * Argumento canônico para a chave de cache. Com `skip: true` (ou `skipToken`) o RTK Query passa
 * o símbolo `skipToken` ao `serializeQueryArgs`: ele segue intocado, senão o normalizador
 * estoura durante o render.
 */
export function canonicalArgs<T>(queryArgs: T, normalize: (args: T) => T): T {
  return (queryArgs as unknown) === skipToken ? queryArgs : normalize(queryArgs);
}

const SENTRY_TITLE = 'MEUS ESTUDOS: linha fora do contrato';

/**
 * Uma linha malformada não derruba a resposta (o adapter a isola e o hook devolve as válidas),
 * mas vai ao Sentry UMA vez por resposta. Só ids e o primeiro motivo; nunca o payload.
 */
export function reportRejected(endpointName: string, rejected: MyStudiesRejectedRow[]): void {
  if (rejected.length === 0) return;

  const ids = rejected.map((row) => (row.id === null ? 'sem id' : String(row.id))).join(', ');
  sendInterfaceErrorToSentry(
    SENTRY_TITLE,
    `${endpointName}: ${rejected.length} linha(s) rejeitada(s) (ids: ${ids}); primeiro motivo: ${rejected[0].reason}`,
    new Error(rejected[0].reason),
    { tags: { endpoint: endpointName }, fingerprint: ['meus-estudos', 'contrato', endpointName] },
  );
}

/**
 * Lista paginada. Cada página é uma entrada própria de cache (sem merge/forceRefetch). O
 * serializeQueryArgs só canoniza a chave: argumentos equivalentes (status em outra ordem, busca
 * com espaço) dividem a entrada.
 */
export function listStudiesEndpoint(
  build: ApiEndpointBuilder,
  cfg: MyStudiesEndpointsConfig,
  endpointName: string,
) {
  return build.query<MyStudiesListResult, MyStudiesQuery>({
    serializeQueryArgs: ({ queryArgs, endpointDefinition, endpointName: name }) => (
      defaultSerializeQueryArgs({
        queryArgs: canonicalArgs(queryArgs, normalizeMyStudiesQuery),
        endpointDefinition,
        endpointName: name,
      })
    ),
    query: (arg) => ({
      url: studiesUrl(cfg.root),
      method: 'GET',
      params: toWireParams(arg),
    }),
    transformResponse: (response: GenericResponse<RawMyStudiesPage>) => {
      // `rejectedCount` avisa a tela de que linhas foram descartadas (total não é decrementado).
      const { list, rejected } = toMyStudiesListResult(response.data);
      reportRejected(endpointName, rejected);
      return list;
    },
    providesTags: [cfg.listTag],
  });
}

/**
 * Retrato leve do status para o polling. Chame com `skip` quando não houver id (um `ids`
 * vazio é 422 no backend) e com no máximo 50 ids; o adapter ordena e remove repetidos,
 * e a chave de cache também.
 */
export function studiesStatusEndpoint(
  build: ApiEndpointBuilder,
  cfg: MyStudiesEndpointsConfig,
  endpointName: string,
) {
  return build.query<MyStudyStatusSnapshot[], number[]>({
    serializeQueryArgs: ({ queryArgs, endpointDefinition, endpointName: name }) => (
      defaultSerializeQueryArgs({
        queryArgs: canonicalArgs(queryArgs, normalizeStudyIds),
        endpointDefinition,
        endpointName: name,
      })
    ),
    query: (ids) => ({
      url: studiesUrl(cfg.root, 'status/'),
      method: 'GET',
      params: toStatusWireParams(ids),
    }),
    transformResponse: (response: GenericResponse<RawMyStudyStatus[]>) => {
      const { snapshots, rejected } = toMyStudyStatusSnapshotsResult(response.data);
      reportRejected(endpointName, rejected);
      return snapshots;
    },
  });
}

/** Resumo de empresas do escopo do usuário (filtro de empresa). */
export function companiesEndpoint(
  build: ApiEndpointBuilder,
  cfg: MyStudiesEndpointsConfig,
  endpointName: string,
) {
  return build.query<StudyCompaniesSummary, void>({
    query: () => ({
      url: studiesUrl(cfg.root, 'empresas/'),
      method: 'GET',
    }),
    transformResponse: (response: GenericResponse<RawStudyCompanies>) => {
      const { summary, rejected } = toStudyCompaniesSummaryResult(response.data);
      reportRejected(endpointName, rejected);
      return summary;
    },
  });
}

/** Opções de criador (filtro de criador). */
export function creatorsEndpoint(
  build: ApiEndpointBuilder,
  cfg: MyStudiesEndpointsConfig,
  endpointName: string,
) {
  return build.query<StudyCreatorOption[], MyStudyCreatorsQuery | void>({
    serializeQueryArgs: ({ queryArgs, endpointDefinition, endpointName: name }) => (
      defaultSerializeQueryArgs({
        queryArgs: canonicalArgs(queryArgs, (args) => normalizeCreatorsQuery(args ?? undefined)),
        endpointDefinition,
        endpointName: name,
      })
    ),
    query: (arg) => ({
      url: studiesUrl(cfg.root, 'criadores/'),
      method: 'GET',
      params: toCreatorsWireParams(arg ?? undefined),
    }),
    transformResponse: (response: GenericResponse<RawStudyCreator[]>) => {
      const { options, rejected } = toStudyCreatorOptionsResult(response.data);
      reportRejected(endpointName, rejected);
      return options;
    },
  });
}
